import { useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useOutsideClick } from '@/hooks'

export interface RecipeOption {
  cardId: number
  title: string
}

interface RecipePickerProps {
  options: readonly RecipeOption[]
  /** Выбранный рецепт или null, если размеры вводятся вручную. */
  value: number | null
  onChange: (cardId: number | null) => void
}

const MANUAL_LABEL = 'Свои размеры'

/**
 * Выбор рецепта, из которого подставляются исходные размеры.
 *
 * Список рисуем сами, а не нативным select: он по-разному выглядит в iOS и
 * Android и не поддаётся стилизации — та же причина, что и в калькуляторе
 * себестоимости.
 */
const RecipePicker = ({ options, value, onChange }: RecipePickerProps) => {
  const [isOpen, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useOutsideClick(rootRef, () => setOpen(false), isOpen)

  const selected = options.find(option => option.cardId === value)

  const select = (cardId: number | null) => {
    onChange(cardId)
    setOpen(false)
  }

  return (
    <div className="shape-calc-recipe" ref={rootRef}>
      <span className="shape-calc-recipe__label">Рецепт</span>

      <button
        type="button"
        className="shape-calc-recipe__button"
        onClick={() => setOpen(open => !open)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{selected ? selected.title : MANUAL_LABEL}</span>
        <ChevronDown size={16} aria-hidden />
      </button>

      {isOpen && (
        <ul className="shape-calc-recipe__list" role="listbox">
          <li>
            <button
              type="button"
              role="option"
              aria-selected={value === null}
              className={`shape-calc-recipe__option ${value === null ? 'active' : ''}`}
              // mousedown, а не click: click срабатывает после blur, и список
              // успевает закрыться раньше, чем выбор долетит.
              onMouseDown={event => {
                event.preventDefault()
                select(null)
              }}
            >
              {MANUAL_LABEL}
            </button>
          </li>

          {options.map(option => (
            <li key={option.cardId}>
              <button
                type="button"
                role="option"
                aria-selected={option.cardId === value}
                className={`shape-calc-recipe__option ${option.cardId === value ? 'active' : ''}`}
                onMouseDown={event => {
                  event.preventDefault()
                  select(option.cardId)
                }}
              >
                {option.title}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default RecipePicker
